import React from 'react';

import { IconSpinner, LayoutComposer } from '../../components/index.js';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer.js';
import FooterContainer from '../FooterContainer/FooterContainer.js';

import { validProps } from './Field';

import SectionBuilder from './SectionBuilder/SectionBuilder.js';
import StaticPage from './StaticPage.js';

import css from './PageBuilder.module.css';
import { useState } from 'react';
import classNames from 'classnames';
import { IoIosArrowUp } from 'react-icons/io';
import { IoIosArrowDown } from 'react-icons/io';
import { FormattedMessage, useIntl } from 'react-intl';

const getMetadata = (meta, schemaType, fieldOptions) => {
  const { pageTitle, pageDescription, socialSharing } = meta;

  // pageTitle is used for <title> tag in addition to page schema for SEO
  const title = validProps(pageTitle, fieldOptions)?.content;
  // pageDescription is used for different <meta> tags in addition to page schema for SEO
  const description = validProps(pageDescription, fieldOptions)?.content;
  // Data used when the page is shared in social media services
  const openGraph = validProps(socialSharing, fieldOptions);
  // We add OpenGraph image as schema image if it exists.
  const schemaImage = openGraph?.images1200?.[0]?.url;
  const schemaImageMaybe = schemaImage ? { image: [schemaImage] } : {};
  const isArticle = ['Article', 'NewsArticle', 'TechArticle'].includes(schemaType);
  const schemaHeadlineMaybe = isArticle ? { headline: title } : {};

  // Schema for search engines (helps them to understand what this page is about)
  // http://schema.org (This template uses JSON-LD format)
  //
  // In addition to this schema data for search engines, src/components/Page/Page.js adds some extra schemas
  // Read more about schema:
  // - https://schema.org/
  // - https://developers.google.com/search/docs/advanced/structured-data/intro-structured-data
  const pageSchemaForSEO = {
    '@context': 'http://schema.org',
    '@type': schemaType || 'WebPage',
    description: description,
    name: title,
    ...schemaHeadlineMaybe,
    ...schemaImageMaybe,
  };

  return {
    title,
    description,
    schema: pageSchemaForSEO,
    socialSharing: openGraph,
  };
};

const LoadingSpinner = () => {
  return (
    <div className={css.loading}>
      <IconSpinner delay={600} />
    </div>
  );
};

//////////////////
// Page Builder //
//////////////////

/**
 * PageBuilder can be used to build content pages using page-asset.json.
 *
 * Note: props can include a lot of things that depend on
 * - pageAssetsData: json asset that contains instructions how to build the page content
 *   - asset should contain an array of _sections_, which might contain _fields_ and an array of _blocks_
 *     - _blocks_ can also contain _fields_
 * - fallbackPage: component. If asset loading fails, this is used instead.
 * - options: extra mapping of 3 level of sub components
 *   - sectionComponents: { ['my-section-type']: { component: MySection } }
 *   - blockComponents: { ['my-component-type']: { component: MyBlock } }
 *   - fieldComponents: { ['my-field-type']: { component: MyField, pickValidProps: data => Number.isInteger(data.content) ? { content: data.content } : {} }
 *     - fields have this pickValidProps as an extra requirement for data validation.
 * - pageProps: props that are passed to src/components/Page/Page.js component
 *
 * @param {Object} props
 * @returns page component
 */
const PageBuilder = props => {
  const {
    pageAssetsData,
    inProgress,
    error,
    fallbackPage,
    schemaType,
    options,
    currentPage,
    fromLandingPage,
    pageId,
    ...pageProps
  } = props;
  const intl = useIntl();

  if (!pageAssetsData && fallbackPage && !inProgress && error) {
    return fallbackPage;
  }

  // Page asset contains UI info and metadata related to it.
  // - "sections" (data that goes inside <body>)
  // - "meta" (which is data that goes inside <head>)
  const { sections = [], meta = {} } = pageAssetsData || {};
  const pageMetaProps = getMetadata(meta, schemaType, options?.fieldComponents);

  const layoutAreas = `
    topbar
    main
    footer
  `;
  const whatsAppLink = (
    <a href={'https://api.whatsapp.com/send/?phone=5511976976506'} target="_blank">
      <FormattedMessage id="PageBuilder.whatsAppLink" />
    </a>
  );

  const appleStoreLink = (
    <a href={'https://apps.apple.com/br/app/my-tattoo-hub/id6753714884'} target="_blank">
      <FormattedMessage id="PageBuilder.appleStoreLink" />
    </a>
  );

  const googlePlayLink = (
    <a
      href={'https://play.google.com/store/apps/details?id=com.mytattoohub&hl=pt_BR'}
      target="_blank"
    >
      <FormattedMessage id="PageBuilder.googlePlayLink" />
    </a>
  );

  const paraTattuadoresLink = (
    <a href={'/p/my-tattoo-hub-para-tatuadores'} target="_self">
      <FormattedMessage id="PageBuilder.paraTattuadoresLink" />
    </a>
  );

  const pricingPageLink = (
    <a href={'/subscription'} target="_self">
      <FormattedMessage id="PageBuilder.pricingPageLink" />
    </a>
  );

  const landingPageFaqs = [
    {
      id: 0,
      qs: intl.formatMessage({ id: 'pageBuilder.landingPageQuestion1' }),
      ans: intl.formatMessage({ id: 'pageBuilder.landingPageAnswer1' }),
      para2: intl.formatMessage({ id: 'pageBuilder.landingPageAnswer1Para2' }),
    },
    {
      id: 1,
      qs: intl.formatMessage({ id: 'pageBuilder.landingPageQuestion2' }),
      ans: intl.formatMessage({ id: 'pageBuilder.landingPageAnswer2' }),
      para2: intl.formatMessage(
        { id: 'pageBuilder.landingPageAnswer2Para2' },
        { whatsAppLink: whatsAppLink }
      ),
    },
    {
      id: 2,
      qs: intl.formatMessage({ id: 'pageBuilder.landingPageQuestion3' }),
      ans: intl.formatMessage({ id: 'pageBuilder.landingPageAnswer3' }),
    },
    {
      id: 3,
      qs: intl.formatMessage({ id: 'pageBuilder.landingPageQuestion4' }),
      ans: intl.formatMessage({ id: 'pageBuilder.landingPageAnswer4' }),
    },
    {
      id: 4,
      qs: intl.formatMessage({ id: 'pageBuilder.landingPageQuestion5' }),
      ans: intl.formatMessage(
        { id: 'pageBuilder.landingPageAnswer5' },
        {
          appleStoreLink: appleStoreLink,
          googlePlayLink: googlePlayLink,
        }
      ),
    },
  ];

  const paraTatuadoresFaqs = [
    {
      id: 0,
      qs: intl.formatMessage({ id: 'pageBuilder.paraTatuadorePageQuestion1' }),
      ans: intl.formatMessage({ id: 'pageBuilder.paraTatuadorePageAnswer1' }),
      para2: intl.formatMessage(
        { id: 'pageBuilder.paraTatuadorePageAnswer1Para2' },
        { paraTattuadoresLink: paraTattuadoresLink }
      ),
    },
    {
      id: 1,
      qs: intl.formatMessage({ id: 'pageBuilder.paraTatuadorePageQuestion2' }),
      ans: intl.formatMessage({ id: 'pageBuilder.paraTatuadorePageAnswer2' }),
    },
    {
      id: 2,
      qs: intl.formatMessage({ id: 'pageBuilder.paraTatuadorePageQuestion3' }),
      ans: intl.formatMessage({ id: 'pageBuilder.paraTatuadorePageAnswer3' }),
      para2: intl.formatMessage(
        { id: 'pageBuilder.paraTatuadorePageAnswer3Para2' },
        { pricingPageLink: pricingPageLink }
      ),
    },
    {
      id: 3,
      qs: intl.formatMessage({ id: 'pageBuilder.paraTatuadorePageQuestion4' }),
      ans: intl.formatMessage({ id: 'pageBuilder.paraTatuadorePageAnswer4' }),
    },
    {
      id: 4,
      qs: intl.formatMessage({ id: 'pageBuilder.paraTatuadorePageQuestion5' }),
      ans: intl.formatMessage(
        { id: 'pageBuilder.paraTatuadorePageAnswer5' },
        { appleStoreLink: appleStoreLink, googlePlayLink: googlePlayLink }
      ),
    },
  ];

  const faqList = fromLandingPage ? landingPageFaqs : paraTatuadoresFaqs;

  const [faqToggler, setFaqToggler] = useState(() => Array(faqList?.length).fill(false));
  return (
    <StaticPage {...pageMetaProps} {...pageProps}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage={currentPage} />
              </Topbar>
              <Main as="main" className={css.main}>
                {sections.length === 0 && inProgress ? (
                  <LoadingSpinner />
                ) : (
                  <SectionBuilder sections={sections} options={options} />
                )}
                {(fromLandingPage || pageId === 'my-tattoo-hub-para-tatuadores') && (
                  <div
                    className={classNames(css.landingPageFaqs, {
                      [css.tattuadoreContainer]: pageId === 'my-tattoo-hub-para-tatuadores',
                    })}
                  >
                    <div className={css.faqHeader}>
                      <FormattedMessage id="PageBuilder.faqHeader" />
                    </div>
                    <div className={css.faqList}>
                      {faqList?.map((faq, idx) => (
                        <div
                          className={classNames(css.faqItem, {
                            [css.lastItem]: idx === faqList.length - 1,
                            [css.faqAnsMarginBottom]: faqToggler?.[idx] === true,
                          })}
                          key={faq.id}
                        >
                          <div
                            className={css.faqTitle}
                            onClick={() => {
                              setFaqToggler(prev => {
                                const newArr = Array(prev.length).fill(false);
                                newArr[idx] = !prev[idx];
                                return newArr;
                              });
                            }}
                          >
                            <div className={css.questionDiv}>{faq?.qs}</div>
                            <div className={faqToggler?.[idx] ? css.rotate : ''}>
                              {/* {faqToggler?.[idx] === false ? <IoIosArrowDown /> : <IoIosArrowUp />} */}
                              <IoIosArrowDown />
                            </div>
                          </div>
                          <div
                            className={classNames(css.faqAnswerWrapper, {
                              [css.open]: faqToggler?.[idx] === true,
                            })}
                          >
                            <div className={css.faqAnswerContent}>
                              <div className={css.faqFirstAns}>{faq?.ans}</div>
                              {faq?.para2 && <div className={css.faqSecAns}>{faq?.para2}</div>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

export { LayoutComposer, StaticPage, SectionBuilder };

export default PageBuilder;
